import cls from 'classnames';
import * as React from 'react';

import { FieldProps } from 'formik';

const styles = require('./InputSelect.scss');

interface Props<T> extends FieldProps {
    label: string;
    placeholder?: string;
    choices: [T, string][];
    disabled?: boolean;
    readonly?: boolean;

    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    customOnChange?: (oldVal: T | null, value: T | null) => void;
}

interface State<T> {
    isOpen: boolean;
    valuePresentation: string;
    selectedValue: T | null;
    choices: [T, string][];
}

export class MaterialSelectInput<T extends string | number> extends React.Component<Props<T>, State<T>> {
    input: HTMLInputElement;

    constructor(props: Props<T>) {
        super(props);

        this.handleFocus = this.handleFocus.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleChange = this.handleChange.bind(this);

        this.state = {
            isOpen: false,
            valuePresentation: '',
            selectedValue: null,
            choices: props.choices,
        };
    }

    componentDidUpdate(prevProps: Props<T>) {
        if (JSON.stringify(prevProps.choices) !== JSON.stringify(this.props.choices)) {
            this.setState({
                choices: this.props.choices,
            });
        }
        if (prevProps.field.value !== this.props.field.value) {
            let targetValuePresentation = this.getValuePersentation(this.props.field.value);
            if (this.state.valuePresentation !== targetValuePresentation) {
                // Rerender w/ new valuePresentation
                this.setState({
                    selectedValue: this.props.field.value,
                    valuePresentation: targetValuePresentation,
                });
            }

            let validChoices = this.props.choices;
            if (!validChoices.find((item) => item[0] === this.props.field.value)) {
                this.resetField();
            }
        }
    }

    componentDidMount() {
        this.setState({
            selectedValue: this.props.field.value,
            valuePresentation: this.getValuePersentation(this.props.field.value),
        });
    }

    handleFocus(e: React.FocusEvent<HTMLInputElement>) {
        const { onFocus, placeholder } = this.props;
        e.currentTarget.placeholder = (placeholder && placeholder) || '';
        onFocus && onFocus(e);

        this.setState({ isOpen: true });
    }

    handleBlur(e: React.SyntheticEvent<HTMLInputElement>) {
        const {
            field: { name },
            form,
        } = this.props;
        e.currentTarget.placeholder = '';

        if (!this.state.selectedValue) {
            e.currentTarget.value = '';
        }

        this.setState({
            isOpen: false,
            valuePresentation: this.state.selectedValue ? this.state.valuePresentation : '',
            choices: this.state.selectedValue ? this.state.choices : this.props.choices,
        });
    }

    handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        const needle = e.currentTarget.value;
        let exactMatch: [T, string] | false = false;

        const newChoices = this.props.choices.filter(([value, label]) => {
            const lowerLabel = label.toLowerCase();
            if (lowerLabel === needle.toLowerCase()) {
                exactMatch = [value, label];
            }
            return lowerLabel.startsWith(needle.toLowerCase());
        });

        if (exactMatch) {
            let match = exactMatch[0] as T; // workaround for weird ts issue
            this.props.field.onChange(match);
        }

        this.setState({
            valuePresentation: exactMatch ? exactMatch[1] : needle,
            choices: newChoices,
            selectedValue: exactMatch ? exactMatch[0] : null,
        });
    }

    render() {
        const {
            label,
            field: { value, name },
            form,
            disabled,
            readonly,
        } = this.props;

        const error = form.errors[name];
        const touched = form.touched[name];
        const { isOpen, valuePresentation, selectedValue, choices } = this.state;
        const hasValue = !!value;

        return (
            <div className={cls(styles.className, { [styles.disabled]: !!disabled })}>
                <input
                    ref={(el: HTMLInputElement) => {
                        this.input = el;
                    }}
                    type='text'
                    onFocus={this.handleFocus}
                    onBlur={this.handleBlur}
                    onChange={this.handleChange}
                    value={valuePresentation}
                    className={cls(styles.input, { [styles.hasError]: touched && error })}
                    autoComplete='disabled'
                    disabled={disabled || false}
                    readOnly={readonly || false}
                />

                <span
                    className={styles.chevron}
                    onClick={(e) => {
                        this.input.focus();
                    }}
                />

                <input value={value || ''} type='hidden' disabled={disabled || false} />

                <span className={styles.bar} />

                <div className={cls(styles.wrapper, { [styles.wrapperOpen]: isOpen })}>
                    {choices.map(([value, label]) => (
                        <div
                            key={value}
                            className={cls(styles.option, { [styles.optionActive]: selectedValue === value })}
                            onMouseDown={(e: React.SyntheticEvent<HTMLDivElement>) => {
                                this.setState(
                                    {
                                        valuePresentation: label,
                                        selectedValue: value,
                                    },
                                    () => {
                                        const oldVal = this.props.field.value;
                                        form.setFieldValue(name, value);
                                        !!this.props.customOnChange && oldVal !== value && this.props.customOnChange(oldVal, value);
                                    }
                                );
                            }}
                        >
                            {label}
                        </div>
                    ))}
                    {choices.length === 0 && <div className={styles.alert}>Nothing found</div>}
                </div>
                <label className={cls(styles.label, { [styles.labelActive]: hasValue })}>{label}</label>
                {touched && error && <span className={styles.error}>{error}</span>}
            </div>
        );
    }

    private getValuePersentation(value: T) {
        let match = this.props.choices.find((value, idx) => value[0] === this.props.field.value);
        if (!!match) {
            return match[1];
        }
        return '';
    }

    private resetField() {
        this.setState(
            {
                valuePresentation: '',
                selectedValue: null,
            },
            () => {
                const oldVal = this.props.field.value;
                this.props.form.setFieldValue(this.props.field.name, null);
                !!this.props.customOnChange && this.props.customOnChange(oldVal, null);
            }
        );
    }
}

export default MaterialSelectInput;
